// グローバルキーボードショートカット。
// - Ctrl + 「+」「-」: 段階ズーム（画面中央が中心）
// - Esc: ツール解除（idle へ戻る）
// - Delete / Backspace: 選択中オブジェクトの削除
// - Ctrl + ] : 選択中オブジェクトを最前面へ（z-order を全体最大 + 1 に更新）
// - Ctrl + [ : 選択中オブジェクトを最背面へ（z-order を全体最小 - 1 に更新）
//
// W/A/S/D は use-keyboard-pan で別途処理する。

import { useEffect } from "react";
import { useCanvasStore } from "../store/canvas-store";
import { useCanvasDocStore } from "../store/canvas-doc-store";
import { stepZoomIn, stepZoomOut, zoomAt } from "../../../lib/canvas-coordinates";
import type { CanvasOpDispatcher } from "../use-canvas-op-dispatcher";

export function useGlobalShortcuts(dispatcher?: CanvasOpDispatcher | undefined): void {
  const setView = useCanvasStore((s) => s.setView);
  const resetMode = useCanvasStore((s) => s.resetMode);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      // contentEditable / input / textarea の中ではグローバル削除ショートカットを発火させない。
      const target = event.target as HTMLElement | null;
      const isEditingTarget =
        target !== null &&
        (target.isContentEditable ||
          target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA");

      if (event.key === "Escape") {
        resetMode();
        return;
      }

      if ((event.key === "Delete" || event.key === "Backspace") && !isEditingTarget) {
        const selected = useCanvasStore.getState().selectedObjectIds;
        if (selected.length > 0 && dispatcher) {
          event.preventDefault();
          for (const id of selected) {
            dispatcher.deleteObject(id);
          }
          useCanvasStore.getState().setSelectedObjectIds([]);
        }
        return;
      }

      if (!(event.ctrlKey || event.metaKey)) return;

      if (event.key === "]") {
        bringForward(dispatcher);
        event.preventDefault();
        return;
      }
      if (event.key === "[") {
        sendBackward(dispatcher);
        event.preventDefault();
        return;
      }

      if (event.key === "+" || event.key === ";" || event.key === "=") {
        // ; や = は Ctrl+= で来る環境がある。いずれもズームインに割り当てる
        event.preventDefault();
        const view = useCanvasStore.getState().view;
        const center = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
        setView(zoomAt(view, center, stepZoomIn(view.zoom)));
      } else if (event.key === "-" || event.key === "_") {
        event.preventDefault();
        const view = useCanvasStore.getState().view;
        const center = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
        setView(zoomAt(view, center, stepZoomOut(view.zoom)));
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [dispatcher, resetMode, setView]);
}

function bringForward(dispatcher: CanvasOpDispatcher | undefined): void {
  if (!dispatcher) return;
  const selected = useCanvasStore.getState().selectedObjectIds;
  if (selected.length === 0) return;
  const objects = useCanvasDocStore.getState().objects;
  const maxZ = Math.max(0, ...Object.values(objects).map((o) => o.zOrder));
  let nextZ = maxZ + 1;
  for (const id of selected) {
    dispatcher.transformObject(id, { zOrder: nextZ });
    nextZ += 1;
  }
}

function sendBackward(dispatcher: CanvasOpDispatcher | undefined): void {
  if (!dispatcher) return;
  const selected = useCanvasStore.getState().selectedObjectIds;
  if (selected.length === 0) return;
  const objects = useCanvasDocStore.getState().objects;
  const minZ = Math.min(0, ...Object.values(objects).map((o) => o.zOrder));
  let nextZ = minZ - 1;
  for (const id of selected) {
    dispatcher.transformObject(id, { zOrder: nextZ });
    nextZ -= 1;
  }
}
